import React, { useEffect, useRef } from 'react';
import { frameTransform } from '../../../utils/mediaFrame';
import PortalCanvas from '../canvas/PortalCanvas';

/**
 * The 9:16 canvas while composing — what the Portal will look like when somebody watches it,
 * drawn by the same PortalCanvas every reader uses, playing the LOCAL file (never an upload).
 *
 * On the COVER tab it pauses and seeks to the frame being chosen; mid-drag on TRIM it holds on
 * the frame under the handle; everywhere else it loops inside the clip. With `editor` the
 * canvas is the layout editor: type, drag, pinch, twist, style, background (handoff v3 §5).
 */
export default function PortalPreview({
  media,              // { kind: 'video'|'photo'|'text', localUrl }
  text = '',
  chip = '',          // the window label under the words ('NOW → 4H')
  layout = null,      // utils/portalLayout — where everything sits
  onLayout = null,    // given → the canvas is the layout editor
  venue = null,       // { name, address, city } once one is added
  venueDate = null,   // { mon, day, time } — the window, for the venue's DATE style
  event = null,       // the linked event row
  editor = {},        // the rest of PortalLayoutEditor's props (editing, selected, onOpen, frame…)
  coverPct = null,    // on the COVER tab: pause here instead of playing
  seeking = false,
  clip = null,        // { startMs, endMs } — play only this slice, and loop inside it
  scrubMs = null,     // TRIM, mid-drag: hold on the frame under the handle being dragged
  frame = null,       // { x, y, scale } — where the photo or clip sits (utils/mediaFrame)
  onPlaySec = null,
  showHeader = false,
  headerTop = 92,     // where the sender's row sits — under the composer's own header
  timeLeft = '4H LEFT',
  authorName = 'you',
  authorAvatarUrl = null,
  durationSec = 0,
  className = '',
  children = null,
}) {
  const videoRef = useRef(null);

  // One seek in flight at a time. A drag asks for a new frame on every pointer move; queueing
  // each of those on a big local file is what makes a scrub lag seconds behind the finger.
  // The newest target waits for the current seek to land, and the ones in between are dropped.
  const seekRef = useRef({ busy: false, target: null });
  const seekTo = (v, t) => {
    const st = seekRef.current;
    if (!Number.isFinite(t)) return;
    st.target = t;
    if (st.busy) return;
    const next = st.target;
    st.target = null;
    if (Math.abs(v.currentTime - next) <= 0.04) return;
    st.busy = true;
    try { v.currentTime = next; } catch { st.busy = false; }
  };
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onSeeked = () => {
      const st = seekRef.current;
      st.busy = false;
      if (st.target != null) seekTo(v, st.target);
    };
    v.addEventListener('seeked', onSeeked);
    return () => v.removeEventListener('seeked', onSeeked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media?.localUrl]);

  // Mid-drag on TRIM the picture follows the handle; COVER pauses on its frame; everything
  // else plays. Letting go of a trim handle starts the clip again from its new beginning.
  const scrubbing = scrubMs != null;
  const scrubbingRef = useRef(false);
  useEffect(() => {
    const v = videoRef.current;
    if (!v || media?.kind !== 'video') return;
    if (scrubbing) {
      scrubbingRef.current = true;
      v.pause();
      seekTo(v, scrubMs / 1000);
      return;
    }
    if (seeking) {
      v.pause();
      const from = clip ? clip.startMs / 1000 : 0;
      const span = clip ? (clip.endMs - clip.startMs) / 1000 : (durationSec || v.duration || 0);
      seekTo(v, from + (coverPct ?? 0) * span);
      return;
    }
    if (scrubbingRef.current) {
      scrubbingRef.current = false;
      if (clip) seekTo(v, clip.startMs / 1000);
    }
    // play() returns a promise everywhere current; wrapped so an old engine's undefined can't throw.
    if (v.paused) Promise.resolve(v.play?.()).catch(() => { /* autoplay policy; the preview just sits on a frame */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrubbing, scrubMs, seeking, coverPct, durationSec, media?.kind, clip]);

  // The Portal IS the clip: playback loops inside it rather than running on into footage
  // nobody chose. Same rule the viewer follows once it's posted.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !clip || clip.endMs <= clip.startMs) return undefined;
    const start = clip.startMs / 1000;
    const end = clip.endMs / 1000;
    const onTime = () => {
      // Mid-drag the frame is wherever the handle is, even past the window's end.
      if (scrubbingRef.current) return;
      onPlaySec?.(v.currentTime);
      if (v.currentTime >= end - 0.06 || v.currentTime < start - 0.1) {
        try { v.currentTime = start; } catch { /* not seekable yet */ }
      }
    };
    v.addEventListener('timeupdate', onTime);
    return () => v.removeEventListener('timeupdate', onTime);
  }, [clip, onPlaySec]);

  // The picture fills the frame, then the sender's pan and zoom nudge it (the rotation is the
  // layout's, and PortalCanvas turns the whole layer).
  const hasPicture = (media?.kind === 'video' || media?.kind === 'photo') && !!media.localUrl;
  const mediaStyle = { objectFit: 'cover', transform: frameTransform(frame, 0), transformOrigin: 'center', willChange: 'transform', pointerEvents: 'none', WebkitTouchCallout: 'none', userSelect: 'none', WebkitUserDrag: 'none' };

  const mediaNode = hasPicture ? (
    media.kind === 'video'
      ? <video ref={videoRef} src={media.localUrl} muted loop playsInline disablePictureInPicture onContextMenu={(e) => e.preventDefault()} className="absolute inset-0 h-full w-full" style={mediaStyle} />
      : <img src={media.localUrl} alt="" draggable={false} onContextMenu={(e) => e.preventDefault()} className="absolute inset-0 h-full w-full" style={mediaStyle} />
  ) : undefined;

  return (
    <PortalCanvas
      mediaNode={mediaNode}
      text={text}
      chip={chip}
      layout={layout}
      venue={venue}
      venueDate={venueDate}
      event={event}
      scrim
      editor={onLayout ? { onLayout, textOnly: !hasPicture, frame, ...editor } : null}
      className={`bg-black ${className}`}
    >
      {showHeader && (
        <div className="absolute z-[3] flex items-center font-mono" style={{ left: 16, top: headerTop, gap: 10, pointerEvents: 'none' }} data-testid="author-header">
          <span className="block flex-shrink-0 overflow-hidden rounded-full bg-[#1A1A1A]" style={{ width: 34, height: 34 }} aria-hidden>
            {authorAvatarUrl && <img src={authorAvatarUrl} alt="" className="h-full w-full object-cover" />}
          </span>
          <span className="min-w-0">
            <span className="block truncate" style={{ fontSize: 13 }}>
              <strong className="text-white">{authorName}</strong>
              <span style={{ marginLeft: 8, color: 'rgba(255,255,255,.7)' }}>now</span>
            </span>
            <span className="block font-bold uppercase" style={{ marginTop: 2, fontSize: 10, letterSpacing: '0.1em', color: '#FF33C6' }}>
              Portal · {timeLeft}
            </span>
          </span>
        </div>
      )}
      {children}
    </PortalCanvas>
  );
}
